import { useEffect } from 'react';
import { keepPreviousData, useInfiniteQuery, useMutation, useQueryClient, type InfiniteData } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import {
  SESSION_LIST_PAGE_SIZE,
  browsePageKey,
  searchPageKey,
  type DeleteManyResult,
  type SearchResponse,
  type SessionListBrowsePage,
  type SessionListQuery,
  type SessionRef,
} from '@localagent/sdk';
import { getSDK } from '../services/sdk-instance';
import { claimsAtom, holdsIdentityAtom } from '../stores/auth/atoms';
import { sdkConfiguredAtom } from '../stores/sdk/atoms';

export const SESSION_LIST_PAGES_KEY = ['session-list-pages'] as const;
export const SESSION_SEARCH_PAGES_KEY = ['session-search-pages'] as const;

function replacePage<P>(old: InfiniteData<P> | undefined, offset: number, page: P): InfiniteData<P> | undefined {
  if (!old) return old;
  const index = old.pageParams.indexOf(offset);
  if (index < 0) return old;
  const pages = [...old.pages];
  pages[index] = page;
  return { ...old, pages };
}

export function useSessionListBrowse(query: SessionListQuery, enabled: boolean) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);
  const accountId = useAtomValue(claimsAtom)?.accountId ?? null;
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!sdkConfigured) return;
    return getSDK().sessionList.onPageRevalidated((revalidation) => {
      if (revalidation.kind !== 'browse') return;
      queryClient.setQueryData<InfiniteData<SessionListBrowsePage>>(
        [...SESSION_LIST_PAGES_KEY, accountId, revalidation.query],
        (old) => replacePage(old, revalidation.offset, revalidation.page),
      );
    });
  }, [sdkConfigured, accountId, queryClient]);

  return useInfiniteQuery<SessionListBrowsePage, Error>({
    queryKey: [...SESSION_LIST_PAGES_KEY, accountId, query],
    queryFn: ({ pageParam }) =>
      getSDK().sessionList.browsePage(query, { offset: pageParam as number }),
    initialPageParam: 0,
    getNextPageParam: (last) => (last.hasMore ? last.nextOffset : undefined),
    placeholderData: keepPreviousData,
    // Synchronous seed from the SDK's list cache: the return
    // leg paints on the first render; the refetch behind it is the background
    // revalidate (stale via initialDataUpdatedAt: 0).
    initialData: () => {
      if (accountId === null) return undefined;
      const seed = getSDK().listCache.peekLast(accountId, browsePageKey(query, 0));
      return seed ? { pages: [seed], pageParams: [0] } : undefined;
    },
    initialDataUpdatedAt: 0,
    enabled: Boolean(isAuthenticated && sdkConfigured && enabled),
  });
}

function trimSearchPage(raw: SearchResponse): SearchResponse & { hasMore: boolean; nextOffset: number } {
  const hasMore = raw.results.length > SESSION_LIST_PAGE_SIZE;
  const results = hasMore ? raw.results.slice(0, SESSION_LIST_PAGE_SIZE) : raw.results;
  return { ...raw, results, total: results.length, hasMore, nextOffset: results.length };
}

export function useSessionListSearch(searchQuery: string, enabled: boolean) {
  const isAuthenticated = useAtomValue(holdsIdentityAtom);
  const sdkConfigured = useAtomValue(sdkConfiguredAtom);
  const accountId = useAtomValue(claimsAtom)?.accountId ?? null;
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!sdkConfigured) return;
    return getSDK().sessionList.onPageRevalidated((revalidation) => {
      if (revalidation.kind !== 'search' || revalidation.params.workspace_id !== undefined) return;
      const trimmed = trimSearchPage(revalidation.page);
      queryClient.setQueryData<InfiniteData<ReturnType<typeof trimSearchPage>>>(
        [...SESSION_SEARCH_PAGES_KEY, accountId, revalidation.params.query],
        (old) => replacePage(old, revalidation.params.offset, {
          ...trimmed,
          nextOffset: revalidation.params.offset + trimmed.results.length,
        }),
      );
    });
  }, [sdkConfigured, accountId, queryClient]);

  return useInfiniteQuery({
    queryKey: [...SESSION_SEARCH_PAGES_KEY, accountId, searchQuery],
    queryFn: async ({ pageParam }) => {
      const offset = pageParam as number;
      const raw = await getSDK().sessionList.searchPage({ query: searchQuery, offset });
      const page = trimSearchPage(raw);
      return { ...page, nextOffset: offset + page.results.length };
    },
    initialPageParam: 0,
    getNextPageParam: (last) => (last.hasMore ? last.nextOffset : undefined),
    placeholderData: keepPreviousData,
    initialData: () => {
      if (accountId === null) return undefined;
      const seed = getSDK().searchCache.peekLast(accountId, searchPageKey({ query: searchQuery, offset: 0 }));
      // The stored page is the RAW SearchResponse; trim to the page shape the
      // queryFn returns.
      return seed
        ? { pages: [trimSearchPage(seed)], pageParams: [0] }
        : undefined;
    },
    initialDataUpdatedAt: 0,
    enabled: Boolean(isAuthenticated && sdkConfigured && enabled && searchQuery !== ''),
  });
}

export function useClearOppositeSessionList(mode: 'browse' | 'search') {
  const queryClient = useQueryClient();
  useEffect(() => {
    if (mode === 'search') {
      queryClient.removeQueries({ queryKey: SESSION_LIST_PAGES_KEY });
    } else {
      queryClient.removeQueries({ queryKey: SESSION_SEARCH_PAGES_KEY });
    }
  }, [mode, queryClient]);
}

export function useDeleteManySessions() {
  const queryClient = useQueryClient();
  return useMutation<DeleteManyResult, Error, SessionRef[]>({
    mutationFn: (items) => getSDK().sessionList.deleteMany(items),
    onSuccess: () => {
      // Mutation-point invalidation call-through (sdk ADR-377c6).
      getSDK().listCache.evictAll();
      getSDK().searchCache.evictAll();
      void queryClient.invalidateQueries({ queryKey: ['sessions'] });
      void queryClient.invalidateQueries({ queryKey: SESSION_LIST_PAGES_KEY });
      void queryClient.invalidateQueries({ queryKey: SESSION_SEARCH_PAGES_KEY });
    },
  });
}
